/**
 * JWT (HS256) 编解码
 *
 * 全部基于 Web Crypto API，无外部依赖。
 *
 * Token 类型：
 * - access：15 分钟，传递给 API 用于鉴权
 * - refresh：30 天，存 KV session:{token} → user_id 支持吊销
 */

import { hmacSha256Buffer } from '../crypto/hmac';
import { JWT_TYPE, TOKEN_TTL } from '../constants';

export interface JwtHeader {
  alg: 'HS256';
  typ: 'JWT';
}

export interface JwtPayload {
  /** 用户 ID */
  sub: number;
  /** 用户名 */
  username: string;
  /** 角色 */
  role: 'admin' | 'user';
  /** token 类型 */
  type: 'access' | 'refresh';
  /** 签发时间（秒） */
  iat: number;
  /** 过期时间（秒） */
  exp: number;
  /** JWT ID（access token 用于黑名单） */
  jti: string;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** base64url 编码（无填充） */
function base64urlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url 解码 */
function base64urlDecode(str: string): Uint8Array {
  const s = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 生成随机 jti */
function generateJti(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** 当前秒数 */
function now(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * 签发 JWT
 */
export async function signJwt(
  payload: Omit<JwtPayload, 'iat' | 'exp' | 'jti'> & { jti?: string; exp?: number; iat?: number },
  secret: string,
): Promise<string> {
  const header: JwtHeader = { alg: 'HS256', typ: 'JWT' };
  const fullPayload: JwtPayload = {
    ...payload,
    iat: payload.iat ?? now(),
    exp: payload.exp ?? now() + TOKEN_TTL.ACCESS,
    jti: payload.jti ?? generateJti(),
  };

  const headerB64 = base64urlEncode(encoder.encode(JSON.stringify(header)));
  const payloadB64 = base64urlEncode(encoder.encode(JSON.stringify(fullPayload)));
  const signingInput = `${headerB64}.${payloadB64}`;

  const sigBuf = await hmacSha256Buffer(secret, signingInput);
  const sigB64 = base64urlEncode(new Uint8Array(sigBuf));

  return `${signingInput}.${sigB64}`;
}

/**
 * 验证 JWT 签名并返回 payload
 *
 * @throws Error 如果签名无效或格式错误
 */
export async function verifyJwt(token: string, secret: string): Promise<JwtPayload> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('invalid token format');
  }
  const [headerB64, payloadB64, sigB64] = parts;
  const signingInput = `${headerB64}.${payloadB64}`;

  const expectedSig = new Uint8Array(await hmacSha256Buffer(secret, signingInput));
  const actualSig = base64urlDecode(sigB64);

  if (expectedSig.length !== actualSig.length) {
    throw new Error('invalid signature');
  }
  let diff = 0;
  for (let i = 0; i < expectedSig.length; i++) {
    diff |= expectedSig[i] ^ actualSig[i];
  }
  if (diff !== 0) {
    throw new Error('invalid signature');
  }

  const payloadJson = decoder.decode(base64urlDecode(payloadB64));
  const payload = JSON.parse(payloadJson) as JwtPayload;

  if (payload.exp < now()) {
    throw new Error('token expired');
  }

  return payload;
}

/**
 * 签发 access + refresh token 对
 */
export async function issueTokenPair(
  user: { id: number; username: string; role: 'admin' | 'user' },
  secret: string,
): Promise<TokenPair & { access_jti: string; refresh_token_value: string }> {
  const accessJti = generateJti();
  const refreshJti = generateJti();
  const ts = now();

  const access_token = await signJwt(
    {
      sub: user.id,
      username: user.username,
      role: user.role,
      type: JWT_TYPE.ACCESS,
      iat: ts,
      exp: ts + TOKEN_TTL.ACCESS,
      jti: accessJti,
    },
    secret,
  );

  const refresh_token = await signJwt(
    {
      sub: user.id,
      username: user.username,
      role: user.role,
      type: JWT_TYPE.REFRESH,
      iat: ts,
      exp: ts + TOKEN_TTL.REFRESH,
      jti: refreshJti,
    },
    secret,
  );

  return {
    access_token,
    refresh_token,
    expires_in: TOKEN_TTL.ACCESS,
    access_jti: accessJti,
    refresh_token_value: refresh_token,
  };
}

/**
 * 从 Authorization header 提取 token
 */
export function extractBearerToken(authHeader: string | null | undefined): string | null {
  if (!authHeader) return null;
  const prefix = 'Bearer ';
  if (!authHeader.startsWith(prefix)) return null;
  return authHeader.slice(prefix.length).trim() || null;
}
